import React, { useState } from 'react';
import { Phone, Mail, MapPin, Calendar, CheckCircle, Loader2 } from 'lucide-react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { PageHero, SectionHeading } from '../components/PageBits';
import { Button } from '../components/ui/button';
import { mockData } from '../data/mock';

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = ['Wedding', 'Quinceañera', 'Birthday Party', 'Pool Party', 'Graduation', 'Corporate / Private', 'Family Celebration', 'Other'];

const Contact = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', event_type: 'Wedding', event_date: '', guest_count: '', message: ''
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(`${API}/api/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Submission failed');
      setStatus('success');
      setForm({ name: '', email: '', phone: '', event_type: 'Wedding', event_date: '', guest_count: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrorMsg('Something went wrong. Please call us directly at (915) 261-5444.');
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <Nav transparent />
      <PageHero
        eyebrow="Let's Plan Your Day"
        title="Check Availability"
        subtitle="Tell us about your vision — Mary personally reviews every inquiry."
        image="/hero/hero-main.jpg"
      />

      <section className="py-20 bg-cream texture-linen">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-12 max-w-6xl">
          {/* Form */}
          <div className="bg-ivory border border-gold/25 rounded-lg p-8 md:p-10 shadow-lg">
            {status === 'success' ? (
              <div className="text-center py-10" data-testid="inquiry-success">
                <CheckCircle className="h-14 w-14 text-gold mx-auto mb-4" />
                <h3 className="font-serif text-2xl text-navy font-bold mb-3">Thank You!</h3>
                <p className="text-slate-700 leading-relaxed">
                  Your inquiry has been received. Mary will personally reach out within 24 hours.
                </p>
                <p className="text-slate-500 text-sm mt-4 italic">
                  In the meantime, feel free to call us directly at (915) 261-5444.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="inquiry-form">
                <div>
                  <h3 className="font-serif text-2xl text-navy font-bold mb-1">Tell us about your event</h3>
                  <p className="text-slate-500 text-sm italic">All fields help us prepare the perfect day for you.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Your Name *" value={form.name} onChange={update('name')} required data-testid="input-name" />
                  <Field label="Phone *" type="tel" value={form.phone} onChange={update('phone')} required data-testid="input-phone" />
                </div>
                <Field label="Email *" type="email" value={form.email} onChange={update('email')} required data-testid="input-email" />

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-navy text-sm font-medium mb-1.5">Event Type</label>
                    <select
                      value={form.event_type}
                      onChange={update('event_type')}
                      className="w-full px-4 py-3 border border-gold/30 rounded-md bg-white text-navy focus:outline-none focus:ring-2 focus:ring-gold/50 text-sm"
                      data-testid="input-event-type"
                    >
                      {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <Field label="Estimated Guest Count" type="number" value={form.guest_count} onChange={update('guest_count')} placeholder="e.g. 80" data-testid="input-guests" />
                </div>

                <Field label="Preferred Event Date" type="date" value={form.event_date} onChange={update('event_date')} data-testid="input-date" />

                <div>
                  <label className="block text-navy text-sm font-medium mb-1.5">Tell us your vision (optional)</label>
                  <textarea
                    value={form.message}
                    onChange={update('message')}
                    rows="4"
                    placeholder="Share any details, questions, or dreams for your event..."
                    className="w-full px-4 py-3 border border-gold/30 rounded-md bg-white text-navy focus:outline-none focus:ring-2 focus:ring-gold/50 text-sm resize-none"
                    data-testid="input-message"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-red-600 text-sm italic" data-testid="inquiry-error">{errorMsg}</p>
                )}

                <Button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full bg-navy hover:bg-navy/90 text-gold border border-gold/60 py-6 text-base tracking-wide shadow-lg disabled:opacity-60"
                  data-testid="submit-inquiry"
                >
                  {status === 'sending' ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>
                  ) : (
                    <><Calendar className="mr-2 h-4 w-4" />Send Inquiry</>
                  )}
                </Button>
              </form>
            )}
          </div>

          {/* Direct info */}
          <div className="space-y-6">
            <SectionHeading eyebrow="Or Reach Out Directly" title="Meet the" accent="Amatos" subtitle="We personally respond to every inquiry. Mornings usually work best." />
            <ContactCard icon={Phone} label="Call Us" primary={mockData.contact.phone} href={`tel:${mockData.contact.phone}`} sub="Mon–Sun · 9 AM – 6 PM" />
            <ContactCard icon={Mail} label="Email Us" primary={mockData.contact.email} href={`mailto:${mockData.contact.email}`} sub="We reply within 24 hours" />
            <ContactCard icon={MapPin} label="Visit Us" primary={mockData.contact.address} sub="Private tours by appointment only" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const Field = ({ label, ...rest }) => (
  <div>
    <label className="block text-navy text-sm font-medium mb-1.5">{label}</label>
    <input
      {...rest}
      className="w-full px-4 py-3 border border-gold/30 rounded-md bg-white text-navy focus:outline-none focus:ring-2 focus:ring-gold/50 text-sm"
    />
  </div>
);

const ContactCard = ({ icon: Icon, label, primary, sub, href }) => {
  const content = (
    <div className="bg-ivory border border-gold/25 rounded-lg p-6 shadow-sm hover:shadow-lg hover:border-gold/60 transition-all flex items-start gap-4">
      <div className="w-12 h-12 rounded-full bg-navy flex items-center justify-center ring-2 ring-gold/40 ring-offset-4 ring-offset-ivory flex-shrink-0">
        <Icon className="h-5 w-5 text-gold" />
      </div>
      <div>
        <p className="text-gold text-xs tracking-[0.25em] uppercase mb-1">{label}</p>
        <p className="text-navy font-serif text-lg">{primary}</p>
        {sub && <p className="text-slate-500 text-xs mt-1 italic">{sub}</p>}
      </div>
    </div>
  );
  return href ? <a href={href} className="block">{content}</a> : content;
};

export default Contact;
